import React, { useContext, useEffect } from 'react';

import {

    BatteryHigh,

    Check,

    Eye,

    House,

    Lightning,

    Minus,

    Plus,

    SunHorizon,

    CaretRight,

} from '@phosphor-icons/react';

import { useDashboardV3Data, type V3ProductCardModel } from './sections/dashboardV3/useDashboardV3Data';

import { roofTextureList } from '../../../../Constants';

import { useOneButtonStore } from '../../../../store/oneButtonStore';

import { ProductFinanceHubRow } from './ProductFinanceHubRow';
import { ProductsFinanceHubContext } from './ProductsFinanceHubContext';

import './oneButton-css/ProductsFinanceHubSection.css';
import { SOLAR_NON_FINALIZED_WARNING } from '../../../../helpers/systemDesignSolarMetrics';



export type SystemDesignProductCardProps = {

    model: V3ProductCardModel;

    onDesign?: () => void;

    onUtility?: () => void;

    onAdders?: () => void;

    onOpenProducts?: () => void;

    onWastageDecrease?: () => void;

    onWastageIncrease?: () => void;

    onShingleClick?: () => void;

    showFinanceProductRow?: boolean;

    financeProductName?: string;

    headEndActions?: React.ReactNode;

};



const ICONS = {

    solar: SunHorizon,

    roof: House,

    battery: BatteryHigh,

    gutter: House,

} as const;



export const SystemDesignProductCard: React.FC<SystemDesignProductCardProps> = ({

    model,

    onDesign,

    onUtility,

    onAdders,

    onOpenProducts,

    onWastageDecrease,

    onWastageIncrease,

    onShingleClick,

    showFinanceProductRow = false,

    financeProductName,

    headEndActions,

}) => {

    const Icon = ICONS[model.kind];

    const showCheck = model.statusTone === 'signed';

    const embedFinanceRow = showFinanceProductRow && !!financeProductName;

    const financeHubCtx = useContext(ProductsFinanceHubContext);
    const hubRowActive =
        embedFinanceRow && financeProductName
            ? !!financeHubCtx?.getRow(financeProductName)?.activeFlag
            : false;



    const opportunity = useOneButtonStore((s) => s.opportunityDetails);

    const systemDesign = useOneButtonStore((s) => s.systemDesign)

    const { solarMetricsOutOfSync } = useDashboardV3Data();
    const showNonFinalizedMetricsWarning = solarMetricsOutOfSync && model.kind === 'solar';


    const shingleMatched = (shingleProductCode: string) => {

        return roofTextureList.find((t) => t.name === shingleProductCode);

    };

    const shingleTexture =
        model.kind === 'roof' && model.shingleSwatch
            ? shingleMatched(model.shingleSwatch)
            : undefined;
    const hasShinglePreview = Boolean(shingleTexture?.img);

    const clickHintIcon = (
        <span className="obd-sd-card__click-hint" aria-hidden>
            <CaretRight size={12} weight="bold" />
        </span>
    );

    const haveProduct = (product: string) => {

        const matched = opportunity?.OpportunityProduct.find(

            (ele: any) => ele.Product_Type_Name.toLowerCase() === product.toLowerCase()

        );

        return !!matched;

    };



    const canOpenDesign = haveProduct(model.kind) || hubRowActive;

    const hasAssignedFinancePartner = (() => {
        const placeholder = /^(select\s+(finance\s+)?partner|select\s+finance|select\s+method)$/i;
        if (embedFinanceRow && financeProductName && financeHubCtx) {
            return financeHubCtx.hasSavedFinancePartnerForProduct(financeProductName);
        }
        const partner = model.financePartner.trim();
        return partner.length > 0 && !placeholder.test(partner);
    })();

    return (

        <article
            className={`obd-sd-card obd-sd-card--${model.kind}${showNonFinalizedMetricsWarning ? ' obd-sd-card--metrics-out-of-sync' : ''}`}
            data-status={model.statusTone}
        >

            <header className="obd-sd-card__head">

                <div className="obd-sd-card__head-main">

                    <span className="obd-sd-card__icon" aria-hidden>

                        <Icon size={20} weight="duotone" />

                    </span>

                    <div className="obd-sd-card__head-text">

                        <h3 className="obd-sd-card__title">{model.title}</h3>

                        <p className="obd-sd-card__subtitle">{model.subtitle}</p>

                    </div>

                </div>

                <div className="obd-sd-card__head-end">
                    {model.statusTone === 'signed' ? (
                        <span className={`obd-sd-card__badge obd-sd-card__badge--${model.statusTone}`}>
                            {showCheck ? <Check size={11} weight="bold" aria-hidden /> : null}
                            {model.statusLabel}
                        </span>
                    ) : null}
                    {headEndActions}
                </div>

            </header>



            <div className="obd-sd-card__body">

                {embedFinanceRow ? (

                    <div className="obd-sd-card__finance-embed">

                        <ProductFinanceHubRow productName={financeProductName} variant="card" />

                    </div>

                ) : (

                    <>
                        {showNonFinalizedMetricsWarning ? (
                            <p className="obd-sd-card__warning" role="status">
                                {SOLAR_NON_FINALIZED_WARNING}
                            </p>
                        ) : null}
                        { model.kind == "solar" && systemDesign && !(systemDesign?.DesignArray != null && systemDesign?.DesignArray.length > 0 && (systemDesign?.DesignArray as any[])[0].ArrayId) && (
                            <div className='design-not-finalized-alert'>
                                Finalize the design in oneDRAW, then refresh and save the Design. Once completed, you can proceed 
                            </div>
                        )}
                        <div className="obd-sd-card__metrics-panel">
                        <dl className="obd-sd-card__metrics">

                            {model.metrics.map((m) => (

                                <div

                                    key={m.label}

                                    className={`obd-sd-card__metric${m.emphasis ? ' obd-sd-card__metric--emphasis' : ''}${m.label.toLowerCase() === 'multiplier' ? ' obd-sd-card__metric--multiplier' : ''}`}

                                >

                                    <dt className="obd-sd-card__metric-label">{m.label}</dt>

                                    <dd className="obd-sd-card__metric-value">{m.value}</dd>

                                </div>

                            ))}

                            {model.kind === 'roof' && model.showWastage ? (
                                <>
                                    <div className="obd-sd-card__metric">
                                        <dt className="obd-sd-card__metric-label">Wastage %</dt>
                                        <dd
                                            className="obd-sd-card__metric-value"
                                            title="Wastage adjustment (coming soon)"
                                        >
                                            {model.wastagePercent != null ? `${model.wastagePercent}%` : '—'}
                                        </dd>
                                    </div>
                                    <button
                                        type="button"
                                        className="obd-sd-card__metric obd-sd-card__metric--action obd-sd-card__metric--shingle-link"
                                        aria-label={`Shingle: ${model.shingleLabel ?? '—'}`}
                                        onClick={onShingleClick}
                                    >
                                        <dt className="obd-sd-card__metric-label">Shingle</dt>
                                        <dd className="obd-sd-card__metric-value">
                                            {model.shingleLabel ?? '—'}
                                        </dd>
                                        {clickHintIcon}
                                    </button>
                                    <button
                                        type="button"
                                        className="obd-sd-card__metric obd-sd-card__metric--action obd-sd-card__metric--shingle-preview"
                                        aria-label={
                                            hasShinglePreview
                                                ? `Shingle preview: ${model.shingleLabel ?? '—'}`
                                                : 'No shingle selected'
                                        }
                                        onClick={onShingleClick}
                                    >
                                        {hasShinglePreview ? (
                                            <span
                                                className="obd-sd-card__shingle-preview-fill"
                                                style={{
                                                    backgroundImage: `url(${shingleTexture!.img})`,
                                                }}
                                                aria-hidden
                                            />
                                        ) : (
                                            <span
                                                className="obd-sd-card__shingle-preview-fill obd-sd-card__shingle-preview-fill--empty"
                                                aria-hidden
                                            >
                                                <span className="obd-sd-card__shingle-preview-placeholder-text">
                                                    NO SHINGLE
                                                </span>
                                            </span>
                                        )}
                                        {clickHintIcon}
                                    </button>
                                </>
                            ) : null}

                        </dl>
                        </div>

                    </>

                )}

            </div>



            <footer className="obd-sd-card__foot">

                {!embedFinanceRow ? (
                    <button
                        type="button"
                        className={`obd-sd-card__finance${hasAssignedFinancePartner ? '' : ' obd-sd-card__finance--needs-partner'}`}
                        onClick={onOpenProducts}
                        aria-label={`Finance: ${model.financePartner}${model.financeMethod ? `, ${model.financeMethod}` : ''}`}
                    >
                        <span className="obd-sd-card__finance-partner">{model.financePartner}</span>
                        {model.financeMethod ? (
                            <span className="obd-sd-card__finance-method">{model.financeMethod}</span>
                        ) : null}
                        {clickHintIcon}
                    </button>
                ) : null}

                {hasAssignedFinancePartner ? (
                    <div className="obd-sd-card__actions" role="group" aria-label="Product actions">
                        <button
                            type="button"
                            className="obd-sd-card__action"
                            onClick={onDesign}
                            disabled={!canOpenDesign}
                        >
                            <Eye size={15} weight="regular" aria-hidden />
                            Design
                        </button>

                        {model.kind === 'solar' ? (
                            <button type="button" className="obd-sd-card__action" onClick={onUtility}>
                                <Lightning size={15} weight="duotone" aria-hidden />
                                Utility
                            </button>
                        ) : null}

                        <button
                            type="button"
                            className="obd-sd-card__action"
                            onClick={onAdders}
                            title="Adders"
                            disabled={!canOpenDesign}
                        >
                            <Plus size={14} weight="bold" aria-hidden />
                            Adders
                        </button>
                    </div>
                ) : null}

            </footer>

        </article>

    );

};

